import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import { Observable, defer, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

import { PhaseType } from 'phase/model/phase.type';
import { Phase } from 'phase/phase';
import { PhaseI18n } from 'phase/phase-i18n';
import { SectionRepositoryPort } from 'phase/section/port/section-repository.port';

import { AppLocator } from '../../app.locator';

export interface PhaseI18nDocument {
	readonly name: string;
	readonly locale: string;
}

export interface PhaseDocument {
	readonly id: string;
	readonly type: string;
	readonly position: number;
	readonly sectionsId: string[];
	readonly organizationId: string;
	readonly updateTimestamp: number;
	readonly localizations: PhaseI18nDocument[];
}

export type PhaseState = EntityState<PhaseDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'phases' })
export class PhaseStore extends EntityStore<PhaseState> {
	constructor() {
		super();
	}

	public get _translateService(): TranslateService {
		return AppLocator.inject(TranslateService);
	}

	public asDocument(phase: Phase): PhaseDocument {
		return phase
			? {
					id: phase.id,
					type: phase.type,
					position: phase.position,
					sectionsId: phase.sectionsId,
					organizationId: phase.organizationId,
					updateTimestamp: phase.updateTimestamp,
					localizations: phase.localizations.map((loc) => ({
						locale: loc.locale,
						name: loc.name,
					})),
			  }
			: null;
	}

	public asEntity(document: PhaseDocument): Phase {
		const sectionRepositoryPort = AppLocator.inject<SectionRepositoryPort>(
			SectionRepositoryPort
		);

		const localizations = !!document
			? (document.localizations || []).map(
					(loc) => new PhaseI18n(loc.name, loc.locale)
			  )
			: [];

		return document
			? new Phase(
					document.id,
					PhaseType[document.type],
					document.position,
					document.sectionsId,
					document.organizationId,
					document.updateTimestamp,
					localizations,
					sectionRepositoryPort.observeSectionByPhaseId(document.id),
					this._observeLocalization(localizations)
			  )
			: null;
	}

	private _observeLocalization<T extends { locale: string }>(
		localizations: ReadonlyArray<T>
	): Observable<T> {
		return localizations && localizations.length > 0
			? defer(() =>
					this._translateService.onLangChange.pipe(
						map((e) => e.lang),
						startWith(this._translateService.currentLang)
					)
			  ).pipe(
					map((lang) => localizations.find((loc) => loc.locale === lang)),
					map((loc) => (!loc ? localizations[0] : loc)),
					shareReplay({ bufferSize: 1, refCount: true })
			  )
			: of(null);
	}
}
